import { type FC } from "react";
import { useTranslation } from "@privacy/translations";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { useUpdateRegistrationAnswers, useUpdateRegistrationStatus } from "../../../api/registrations";
import * as FormDialog from "../../../shared/FormDialog";
import { CircularProgress } from "@privacy/ui";
import Stack from "@mui/material/Stack";

type RegistrationMinorOrMajorChangeDialogProps = {
  onClose: () => void;
  registrationId: string;
  answerRequestMutation: ReturnType<typeof useUpdateRegistrationAnswers>;
};

const DIALOG_HEIGHT = "300px";

export const RegistrationMinorOrMajorChangeDialog: FC<RegistrationMinorOrMajorChangeDialogProps> = ({
  onClose,
  registrationId,
  answerRequestMutation,
}) => {
  const { t } = useTranslation();
  const updateStatusMutation = useUpdateRegistrationStatus(registrationId);

  if (answerRequestMutation.isPending)
    return (
      <FormDialog.Dialog isOpen sx={{ height: DIALOG_HEIGHT }}>
        <Stack sx={{ height: "100%", alignItems: "center", justifyContent: "center" }}>
          <CircularProgress />
        </Stack>
      </FormDialog.Dialog>
    );

  return (
    <FormDialog.Dialog isOpen sx={{ height: DIALOG_HEIGHT }}>
      <FormDialog.Title>{t("RegistrationDrawer.MinorOrMajorChangeDialog.Title")}</FormDialog.Title>
      <FormDialog.Content>
        <FormDialog.FormError
          isError={Boolean(updateStatusMutation.error)}
          errorMessage={t("RegistrationDrawer.MinorOrMajorChangeDialog.Error")}
        />
        <Typography
          sx={theme => ({ fontSize: theme.typography.pxToRem(14), color: theme.palette.text.secondary })}
          variant="body2"
        >
          {t("RegistrationDrawer.MinorOrMajorChangeDialog.Content")}
        </Typography>
      </FormDialog.Content>

      <FormDialog.ActionButtons>
        <Button variant="outlined" onClick={onClose}>
          {t("RegistrationDrawer.MinorOrMajorChangeDialog.MinorAdjustment")}
        </Button>

        <Button
          loading={updateStatusMutation.isPending}
          onClick={() => updateStatusMutation.mutate({ status: "draft" }, { onSuccess: onClose })}
        >
          {t("RegistrationDrawer.MinorOrMajorChangeDialog.RequireNewApproval")}
        </Button>
      </FormDialog.ActionButtons>
    </FormDialog.Dialog>
  );
};
